/**
 * @fileoverview Componente molecule FindChat para búsqueda y selección de clientes en el chat.
 * Permite buscar clientes por nombre, teléfono o correo y seleccionar uno para iniciar conversación.
 * Optimizado con React.memo, useCallback y useMemo para performance.
 *
 * @module FindChat
 * @category Component - Molecule
 * @since 1.0.0
 * @version 1.1.0 - Documentación JSDoc completa y optimización de renderizado.
 */

import React, { useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

import { fetchCustomersByPermission } from '../../../actions/customers';
import { whatsappRecipientLabel } from '../../../util/whatsappMessageUtils';
import SearchComponent from '../../components/SearchMC';


/**
 * @typedef {Object} FindChatProps
 * @description Propiedades del componente FindChat.
 * @property {function} onCustomerSelect - Callback para seleccionar un cliente
 */

/**
 * Componente molecule para búsqueda y selección de clientes en el chat.
 *
 * @component FindChat
 * @category Component - Molecule
 *
 * @description
 * **Características:**
 * - Búsqueda asíncrona de clientes por nombre, teléfono o correo
 * - Selección de cliente para iniciar conversación
 * - Optimizado con React.memo, useCallback y useMemo para evitar renders innecesarios
 * - Estilizado con TailwindCSS
 *
 * **Atomic Design:**
 * - Nivel: Molecule
 * - Composición: Usa SearchComponent (atom) y renderiza lista de clientes
 * - Dependencias: SearchComponent, Redux (useDispatch, useSelector)
 *
 * @param {FindChatProps} props - Propiedades del componente
 * @returns {JSX.Element} Elemento renderizado con buscador y lista de clientes
 *
 * @example
 * <FindChat onCustomerSelect={fn} />
 *
 * @see {@link ../../components/SearchMC} Componente de búsqueda
 * @see {@link ../../../actions/customers} Acción para obtener clientes
 *
 * @since 1.0.0
 * @version 1.1.0 - Documentación JSDoc completa y optimización
 */
const FindChat = React.memo(({ onCustomerSelect }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('NewChat');
  const { customers } = useSelector((state) => state.customersReducer);

  /**
   * Efecto para cargar la lista de clientes al montar el componente.
   * @memberof FindChat
   */
  useEffect(() => {
    dispatch(fetchCustomersByPermission());
  }, [dispatch]);

  /**
   * Callback para buscar clientes por valor ingresado.
   * @function handleSearch
   * @memberof FindChat
   * @param {string} searchValue - Valor de búsqueda
   */
  const handleSearch = useCallback((searchValue) => {
    dispatch(fetchCustomersByPermission(searchValue));
  }, [dispatch]);

  /**
   * Callback para seleccionar un cliente de la lista.
   * @function selectCustomer
   * @memberof FindChat
   * @param {Object} customer - Cliente seleccionado
   */
  const selectCustomer = useCallback((customer) => {
    if (onCustomerSelect) {
      onCustomerSelect(customer);
    }
  }, [onCustomerSelect]);

  const recipientLabels = useMemo(() => ({
    userId: t('customer.withUserId'),
    parentUserId: t('customer.withParentUserId')
  }), [t]);

  /**
   * Memoiza el renderizado de la lista de clientes para performance.
   * @function renderedCustomers
   * @memberof FindChat
   * @returns {JSX.Element[]} Elementos renderizados de clientes
   */
  const renderedCustomers = useMemo(() => (
    customers && customers.map((customer) => (
      <div
        key={customer.id}
        onClick={() => selectCustomer(customer)}
        className="tw-border-b tw-border-gray-200 tw-py-4 tw-cursor-pointer hover:tw-bg-gray-50 tw-transition-colors tw-duration-150"
      >
        <dt className="tw-font-medium tw-text-gray-900 tw-text-sm">{customer.name}</dt>
        <dd className="tw-text-sm tw-text-gray-600 tw-mt-1">
          {whatsappRecipientLabel(customer, recipientLabels)}
        </dd>
      </div>
    ))
  ), [customers, recipientLabels, selectCustomer]);

  /**
   * Renderiza el componente de búsqueda y lista de clientes.
   * @memberof FindChat
   * @returns {JSX.Element}
   */
  return (
    <div className="tw-flex-1">
      <div className="tw-space-y-4">
        <SearchComponent
          placeholder="Buscar por nombre, teléfono o correo"
          debounceTime={700}
          onChange={handleSearch}
          inputClassName="tw-py-3 tw-text-sm focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent"
          mainContainerClassName=""
        />

        <div className="tw-mt-6">
          <div className="tw-text-base tw-font-semibold tw-mb-2 tw-text-gray-900">
            Últimos contactos
          </div>

          <div className="tw-space-y-0">
            {renderedCustomers}
          </div>
        </div>
      </div>
    </div>
  );
});


/**
 * Exportación principal del componente FindChat.
 * @type {React.MemoExoticComponent}
 */
export default FindChat;
